import { Procedure } from "../../../../types/procedures";
import { ProcedureFlags } from "../../../../enums/ProcedureFlags";
import { SpecialBadgeColorValue } from "../../../../enums/SpecialBadgeColor";

// Single source of truth for "is this a limited-time SPECIAL?" or "is this
// FEATURED?" — used by the pill (ProcedureCard / ProcedureMobileListItem) AND
// the pinned-to-top sort (useGetProceduresByType / QuoteProceduresListAll /
// QuoteProceduresListFocused) so the two can never disagree. (Visibility is
// gated separately by the active window in QuoteProvider; this is purely the
// SPECIAL / FEATURED flags.)
export function procedureIsSpecial(procedure: Procedure): boolean {
  return !!procedure.flags?.includes(ProcedureFlags.SPECIAL);
}

export function procedureIsFeatured(procedure: Procedure): boolean {
  return !!procedure.flags?.includes(ProcedureFlags.FEATURED);
}

// Single source of truth for the pinned-to-top ordering, mirroring the iOS
// Explore carousel (ExploreViewModel.carouselProcedures): specials first,
// then featured-only, then everything else — a procedure carrying BOTH flags
// counts as a special (iOS dedupe rule), never double-pinned.
export function procedurePinRank(procedure: Procedure): number {
  if (procedureIsSpecial(procedure)) return 0;
  if (procedureIsFeatured(procedure)) return 1;
  return 2;
}

// CSS modifier suffix shared by the desktop pill
// (`.procedure-special-banner--<key>`) and the mobile label
// (`.grid-item-special-label--<key>`). See quote-procedures-list.css.
export type SpecialBadgeColorKey = "gold" | "green" | "red";

// Maps the stored badge color (GraphQL SpecialBadgeColor) to its CSS modifier
// suffix. null / unknown → gold (the historical default).
export function specialBadgeColorKey(
  color: SpecialBadgeColorValue | null | undefined,
): SpecialBadgeColorKey {
  switch (color) {
    case "GREEN":
      return "green";
    case "RED":
      return "red";
    default:
      return "gold";
  }
}

// Resolves the pill label for the current locale — used for both the
// SPECIAL "limited time offer" pill and the FEATURED pill, mirroring the
// tagline locale-pick (see SignatureProcedureCard): Spanish override →
// English override → the localized i18n default passed in as `fallback`
// (callers pass the appropriate fallback for whichever pill they're rendering).
// Narrowed to just the two fields it reads (not the full `Procedure`) so
// callers outside the funnel (e.g. the admin Estimator) can pass a Relay row
// without a cast — both funnel call sites (ProcedureCard, ProcedureMobileListItem)
// still pass a full `Procedure`, which satisfies this narrower shape as-is.
export function specialBadgeText(
  procedure: Pick<Procedure, "specialBadgeText" | "specialBadgeTextEs">,
  language: string | undefined,
  fallback: string,
): string {
  const localized = language === "es" ? procedure.specialBadgeTextEs : null;
  return (
    (localized && localized.trim()) ||
    (procedure.specialBadgeText && procedure.specialBadgeText.trim()) ||
    fallback
  );
}
